export interface TeamMemberColumn {
    key: string;
    label: string;
    sortKey: string;
    group: 'Team Member' | 'Client' | 'Campaign';
}

// All available columns for the Team Member results table, carried over from
// the legacy report's display-column checkboxes (HHLTB-2800). Single source of
// truth shared by TeamMemberAccordionItem (picker) and TeamMemberSearchResults
// (table + CSV export). `key` matches a serializer field; `sortKey` matches a
// key in SORT_FIELD_MAP on the backend view.
export const ALL_AVAILABLE_COLUMNS: TeamMemberColumn[] = [
    // Team Member
    { key: 'last_name', label: 'Last Name', sortKey: 'last_name', group: 'Team Member' },
    { key: 'first_name', label: 'First Name', sortKey: 'first_name', group: 'Team Member' },
    { key: 'middle_name', label: 'Middle Name', sortKey: 'middle_name', group: 'Team Member' },
    { key: 'role', label: 'Role', sortKey: 'role', group: 'Team Member' },
    { key: 'relationship', label: 'Relationship to Client', sortKey: 'relationship', group: 'Team Member' },
    { key: 'email', label: 'Email', sortKey: 'email', group: 'Team Member' },
    { key: 'phone', label: 'Phone', sortKey: 'phone', group: 'Team Member' },
    { key: 'address', label: 'Street Address', sortKey: 'address', group: 'Team Member' },
    { key: 'city', label: 'City', sortKey: 'city', group: 'Team Member' },
    { key: 'state', label: 'State', sortKey: 'state', group: 'Team Member' },
    { key: 'zip_code', label: 'Zip', sortKey: 'zip_code', group: 'Team Member' },

    // Client
    { key: 'patient_last_name', label: 'Client Last Name', sortKey: 'patient_last_name', group: 'Client' },
    { key: 'patient_first_name', label: 'Client First Name', sortKey: 'patient_first_name', group: 'Client' },
    { key: 'patient_middle_name', label: 'Client Middle Name', sortKey: 'patient_middle_name', group: 'Client' },
    { key: 'patient_email', label: 'Client Email', sortKey: 'patient_email', group: 'Client' },
    { key: 'patient_phone', label: 'Client Phone', sortKey: 'patient_phone', group: 'Client' },
    { key: 'patient_address', label: 'Client Street Address', sortKey: 'patient_address', group: 'Client' },
    { key: 'patient_city', label: 'Client City', sortKey: 'patient_city', group: 'Client' },
    { key: 'patient_state', label: 'Client State', sortKey: 'patient_state', group: 'Client' },
    { key: 'patient_zip_code', label: 'Client Zip', sortKey: 'patient_zip_code', group: 'Client' },
    { key: 'patient_gender', label: 'Client Gender', sortKey: 'patient_gender', group: 'Client' },
    { key: 'patient_birth_date', label: 'Client Date of Birth', sortKey: 'patient_birth_date', group: 'Client' },
    { key: 'patient_death_date', label: 'Client Date of Death', sortKey: 'patient_death_date', group: 'Client' },
    { key: 'patient_preferred_language', label: 'Preferred Language', sortKey: 'patient_preferred_language', group: 'Client' },

    // Campaign
    { key: 'campaign_status', label: 'Campaign Status', sortKey: 'campaign_status', group: 'Campaign' },
    { key: 'campaign_status_date', label: 'Campaign Status Date', sortKey: 'campaign_status_date', group: 'Campaign' },
    { key: 'region', label: 'Region', sortKey: 'region', group: 'Campaign' },
    { key: 'medical_reason', label: 'Medical Reason', sortKey: 'medical_reason', group: 'Campaign' },
    { key: 'primary_medical_issue', label: 'Type (1)', sortKey: 'primary_medical_issue', group: 'Campaign' },
    { key: 'secondary_medical_issue', label: 'Type (2)', sortKey: 'secondary_medical_issue', group: 'Campaign' },
    { key: 'created_date', label: 'Created Date', sortKey: 'created_date', group: 'Campaign' },
];

export const COLUMN_GROUPS: TeamMemberColumn['group'][] = [
    'Team Member',
    'Client',
    'Campaign',
];

// Matches the legacy report's initially-checked columns.
export const DEFAULT_VISIBLE_COLUMNS: string[] = [
    'last_name',
    'first_name',
    'role',
    'relationship',
    'email',
    'patient_last_name',
    'patient_first_name',
    'patient_email',
    'medical_reason',
];

// Parse a `visible_columns` URL value (comma-joined keys) into a validated key
// list. Returns DEFAULT_VISIBLE_COLUMNS when missing or yields no valid keys.
export const parseVisibleColumns = (raw: string | null): string[] => {
    if (raw) {
        const valid = new Set(ALL_AVAILABLE_COLUMNS.map(c => c.key));
        const parsed = raw.split(',').map(s => s.trim()).filter(k => valid.has(k));
        if (parsed.length) return parsed;
    }
    return DEFAULT_VISIBLE_COLUMNS;
};

// True when `selected` is exactly the default set (order-independent).
export const isDefaultSelection = (selected: string[]): boolean =>
    selected.length === DEFAULT_VISIBLE_COLUMNS.length &&
    selected.every(k => DEFAULT_VISIBLE_COLUMNS.includes(k));
